import { DOTS } from '@components/constants';
import { useMemo } from 'react';
import { UsePaginationProps } from './usePagination.types';

const range = (start: number, end: number): number[] => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export const usePagination = (props: UsePaginationProps): Array<string | number> | undefined => {
  const { totalCount, pageSize, siblingCount = 1, currentPage } = props;

  const FIRST_PAGE_INDEX = 1;
  const PAGE_NUMBERS: number = 5;
  const RANGE_START: number = 1;
  const SIBLING_SPACES: number = 2;
  const DOTS_SPACES: number = 3;

  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);
    const totalPageNumbers = siblingCount + PAGE_NUMBERS;

    if (totalPageNumbers >= totalPageCount) {
      return range(RANGE_START, totalPageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, RANGE_START);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > SIBLING_SPACES;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - SIBLING_SPACES;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = DOTS_SPACES + SIBLING_SPACES * siblingCount;
      const leftRange = range(RANGE_START, leftItemCount);
      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = DOTS_SPACES + SIBLING_SPACES * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + RANGE_START, totalPageCount);
      return [FIRST_PAGE_INDEX, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [FIRST_PAGE_INDEX, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
};
